/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React from "react";

import Link from "next/link";
import { isElectron } from "react-device-detect";
import styled from "styled-components";

import { BasePlate } from "src/classes/plate/BasePlate/BasePlate";
import { ContextMenu as ThumbnailContextMenu } from "src/components/Shared/PageThumbnail/ContextMenu";
import { PageScreenshot } from "src/components/Shared/PageThumbnail/PageScreenshot";
import { PageThumbnailTitle } from "src/components/Shared/PageThumbnail/PageThumbnailTitle";
import { ConditionalWrapper } from "src/components/UI/ConditionalWrapper/ConditionalWrapper";
import { UnstyledLink } from "src/components/UI/Links";

interface Props {
  plate: BasePlate;
  filterValue?: string;
  wrapperStyles?: React.CSSProperties;
  screenshotStyles?: React.CSSProperties;
  disableContextMenu?: boolean;
  disableLink?: boolean;
  disableTitle?: boolean;
  truncateTitle?: boolean;
  useBigImage?: boolean;
}

/**
 * Displays a page screenshot and title
 */
export function PageThumbnail({
  plate,
  filterValue,
  wrapperStyles,
  disableContextMenu,
  truncateTitle,
  disableLink,
  screenshotStyles,
  useBigImage,
  disableTitle,
}: Props) {
  const isPublic = plate.observe((state) => state.isPublic);
  return (
    <PreviewWrapper disableLink={disableLink} style={wrapperStyles}>
      <ConditionalWrapper
        condition={!disableContextMenu}
        wrapper={(children) => ThumbnailContextMenu({ plate, children })}
      >
        <>
          <ConditionalWrapper
            condition={!disableLink}
            wrapper={(children) =>
              isElectron ? (
                <UnstyledLink href={plate.internalLink}>{children}</UnstyledLink>
              ) : (
                <Link href={plate.internalLink} shallow={true}>
                  <div>{children}</div>
                </Link>
              )
            }
          >
            <PageScreenshot plate={plate} useBigImage={useBigImage} styles={screenshotStyles} />
          </ConditionalWrapper>
          {!disableTitle && (
            <PageThumbnailTitle
              plate={plate}
              filterValue={filterValue}
              shouldTruncate={truncateTitle}
              disableLink={disableLink}
              isPublic={isPublic}
            />
          )}
        </>
      </ConditionalWrapper>
    </PreviewWrapper>
  );
}

export const PreviewWrapper = styled.div<{ disableLink?: boolean }>`
  display: flex;
  flex-direction: column;
  user-select: none;
  cursor: ${({ disableLink }) => (disableLink ? "default" : "pointer")};
  :hover {
    text-decoration: ${({ disableLink }) => (disableLink ? "default" : "underline")};
  }
`;
